// Native chrome for the dashboard's in-page overlays: the celebration cards
// (welcome / streak / goal / cancel-feedback), the activation-arc progress card,
// the rating ask, the streak +1 card and the Due today deck.
//
// All of them sit under native UIKit chrome that a web scrim cannot cover — the
// bottom tab bar is a real view above the web layer, and the nav bar gets a
// native dimming overlay parented to the UINavigationController. Every card
// used to hand-roll the same four helpers to drive them, which is how the two
// bugs below got in: each copy had to remember the ordering rules on its own,
// and the ones bound to a navigating CTA did not.
//
// Two rules live here so no card has to remember them:
//
//   1. A card that NAVIGATES must let the undim message drain before the visit
//      starts. undim() reaches WebKit synchronously, but WebKit delivers script
//      messages to the native main thread asynchronously; visit in the same
//      tick and the message is routed to the destination's bridge delegate,
//      which carries no nav-bar component (it is mounted on budgets/index
//      alone) and is dropped. The overlay then outlives the push and swallows
//      every touch on the bar, on every screen, until the app restarts.
//      closeChromeThenNavigate() is the only sanctioned way out.
//
//   2. The dim belongs to whichever overlay is actually on screen, so the state
//      is derivable from the DOM at any moment — see openOverlayScrim(), which
//      bridge--nav-bar uses to re-sync on every connect.

const BOTTOM_NAV_SELECTOR = "[data-controller~='bridge--bottom-nav']"
const NAV_BAR_SELECTOR = "[data-controller~='bridge--nav-bar']"

// A card's own scrim, whichever family it belongs to. Matched inside the card
// rather than document-wide, so a card reads its own colour even while another
// one is still in the DOM.
const OVERLAY_SCRIM_SELECTOR =
  ".welcome-cel-scrim, .streak-cel-scrim, .setup-prog-scrim, .rate-ask-scrim, .streak-up-scrim, .goal-reached-scrim, .due-deck-scrim, .bank-review-deck-scrim"

// Every overlay that dims the nav bar, paired with its own scrim. `:not([hidden])`
// is what makes this readable as state: a card is closed the moment it carries
// the attribute, which is why .streak-cel needed an explicit `[hidden]` rule —
// its `display: flex` outranks the UA one, so `hidden` alone left it on screen
// AND matching here.
export const OPEN_OVERLAY_SCRIM_SELECTOR = [
  ".welcome-cel:not([hidden]) .welcome-cel-scrim",
  ".streak-cel:not([hidden]) .streak-cel-scrim",
  ".setup-prog:not([hidden]) .setup-prog-scrim",
  ".rate-ask:not([hidden]) .rate-ask-scrim",
  ".streak-up:not([hidden]) .streak-up-scrim",
  ".goal-reached:not([hidden]) .goal-reached-scrim",
  ".due-deck:not([hidden]) .due-deck-scrim",
  ".bank-review-deck:not([hidden]) .bank-review-deck-scrim"
].join(", ")

// The scrim of whatever overlay is open right now, or null when the screen is
// clear.
export function openOverlayScrim() {
  return document.querySelector(OPEN_OVERLAY_SCRIM_SELECTOR)
}

// A scrim's computed colour, in the shape NavBarComponent.swift decodes. The
// scrim is the single source of truth so the native bar matches the in-page
// dim exactly in both themes.
export function scrimColorOf(scrim) {
  if (!scrim) return {}

  const match = getComputedStyle(scrim).backgroundColor.match(/rgba?\(([^)]+)\)/)
  if (!match) return {}

  const [r, g, b, a] = match[1].split(",").map((part) => parseFloat(part.trim()))
  return { r, g, b, a: a === undefined ? 1 : a }
}

export function bottomNavBridge(controller) {
  return bridgeFor(controller, BOTTOM_NAV_SELECTOR, "bridge--bottom-nav")
}

export function navBarBridge(controller) {
  return bridgeFor(controller, NAV_BAR_SELECTOR, "bridge--nav-bar")
}

// Raise the chrome for a card that has just opened: the tab bar goes away so
// the FAB does not bleed over the scrim, and the nav bar dims to match it.
export function openChrome(controller, root) {
  bottomNavBridge(controller)?.hide()
  navBarBridge(controller)?.dim(scrimColorOf(root.querySelector(OVERLAY_SCRIM_SELECTOR)))
}

// Put the chrome back. `showBottomNav: false` is for a card that hands the tab
// bar straight to something else which is already managing it (the welcome
// card's two CTAs open the voice sheet, which hides it for itself) — showing it
// in between would flash the bar for a frame under the incoming sheet.
export function closeChrome(controller, { showBottomNav = true } = {}) {
  navBarBridge(controller)?.undim()
  if (showBottomNav) bottomNavBridge(controller)?.show()
}

// Rule 1 above: close the chrome, give the native side one frame to drain the
// undim, then navigate. One frame is imperceptible and is what keeps the dim
// from outliving the page that raised it.
export function closeChromeThenNavigate(controller, navigate, options = {}) {
  closeChrome(controller, options)
  requestAnimationFrame(navigate)
}

function bridgeFor(controller, selector, identifier) {
  const element = document.querySelector(selector)
  if (!element) return null

  return controller.application.getControllerForElementAndIdentifier(element, identifier)
}
